/**
 * Snippet and SERP helpers for /start/:slug topic pages.
 *
 * @see https://developers.google.com/search/docs/appearance/snippet
 */
import { Platform } from 'react-native';
import { isGuideHowToBlock } from '@/utils/topic_page_copy';

/** Typical Google SERP description display length. */
export const SERP_DESCRIPTION_MAX = 160;

const TOPIC_MEDIA_CDN = 'https://cdn.gab.ai';

/** Turn a relative CDN key into an absolute URL (pass-through if already absolute). */
export function absolutizeTopicMediaUrl(pathOrUrl) {
  const raw = String(pathOrUrl || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  return `${TOPIC_MEDIA_CDN.replace(/\/$/, '')}/${raw.replace(/^\//, '')}`;
}

/** Prefer the 1200×630 social card; fall back to the square avatar. */
export function resolveTopicShareImage(topic) {
  const og = String(topic?.meta?.ogImage || '').trim();
  if (og) return absolutizeTopicMediaUrl(og);
  const avatar = String(topic?.avatarPath || '').trim();
  return avatar ? absolutizeTopicMediaUrl(avatar) : null;
}

/** Web-only `data-nosnippet` for UI chrome that should not appear in snippets. */
export function snippetExcludeWebProps() {
  return Platform.OS === 'web' ? { dataSet: { nosnippet: '' } } : {};
}

export function truncateSerpDescription(text, max = SERP_DESCRIPTION_MAX) {
  const t = String(text || '').trim();
  if (!t || t.length <= max) return t;
  const cut = t.slice(0, max - 1);
  const lastSpace = cut.lastIndexOf(' ');
  const trimmed = (lastSpace > Math.floor(max * 0.5) ? cut.slice(0, lastSpace) : cut).trimEnd();
  return `${trimmed}…`;
}

function firstOverviewCopy(pageCopyBlocks) {
  const block = (pageCopyBlocks || []).find(
    (row) => row?.type === 'section'
      && !isGuideHowToBlock(row)
      && String(row.copy || row.subtitle || '').trim(),
  );
  return block ? String(block.copy || block.subtitle).trim() : '';
}

/**
 * Build a unique, page-specific meta description for topic SERP snippets.
 * Prefers authored meta; otherwise synthesizes from hero copy and overview.
 */
export function buildTopicSerpDescription({
  topic,
  pageCopyBlocks,
  fallback = '',
}) {
  const fromMeta = String(topic?.meta?.description || '').trim();
  if (fromMeta) return truncateSerpDescription(fromMeta);

  const fromHero = String(topic?.description || '').trim();
  const overviewCopy = firstOverviewCopy(pageCopyBlocks);
  const name = String(topic?.name || '').trim();
  const category = String(topic?.category?.name || '').trim();
  const tags = (Array.isArray(topic?.tags) ? topic.tags : [])
    .map((tag) => String(tag?.name || '').trim())
    .filter(Boolean)
    .slice(0, 2);

  let synthesized = '';
  if (overviewCopy) {
    synthesized = overviewCopy;
  } else if (fromHero.length >= 40) {
    synthesized = fromHero;
  } else if (name && category) {
    synthesized = `Learn about ${name} — ${category} reference guide on Gab AI. Explore key facts, context, and starter questions.`;
  } else if (name) {
    synthesized = `Learn about ${name} — reference guide on Gab AI. Explore key facts, context, and starter questions.`;
  } else {
    synthesized = fallback;
  }

  if (tags.length && synthesized.length < SERP_DESCRIPTION_MAX - 24) {
    synthesized = `${synthesized.replace(/\s+$/, '')} Tags: ${tags.join(', ')}.`;
  }

  return truncateSerpDescription(synthesized || fallback);
}
